import {Component, computed, inject, Input, OnInit, Signal, ViewChild} from '@angular/core';
import {FormGroup, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatTable, MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {OrganizationService} from '@middleware';
import {FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {UserService} from '@services/user/user.service';
import {OrganizationalEmailValidatorMessage, SetOrganizations} from '@validators/org-email.validator';
import {lastValueFrom} from 'rxjs';

@Component({
  imports: [ReactiveFormsModule, FormlyModule, MatIconModule, MatButtonModule, MatDialogModule],
  template: `<h1 mat-dialog-title>Add Owner</h1>
    <mat-dialog-content>
      <form [formGroup]="form" (ngSubmit)="submit($event)" ngNativeValidate #formElement>
        <formly-form [model]="model" [fields]="formFields" [options]="options" [form]="form"></formly-form>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button type="button" [mat-dialog-close]="undefined"
        ><mat-icon class="material-symbols-outlined">cancel</mat-icon> Cancel</button
      >
      <button matButton="filled" type="button" (click)="submit($event)">
        <mat-icon class="material-symbols-outlined">check</mat-icon> Save</button
      >
    </mat-dialog-actions>`,
})
class AddOwnerDialog {
  constructor(private readonly dialogRef: MatDialogRef<AddOwnerDialog, string>) {}
  model = {email: ''};
  formFields: FormlyFieldConfig[] = [
    {
      key: 'email',
      type: 'input',
      props: {
        label: 'Email',
        type: 'email',
        required: true,
      },
      validators: {
        validation: ['email', 'org-email'],
      },
      validation: {
        messages: {
          email: 'A valid e-mail address is required.',
          'org-email': OrganizationalEmailValidatorMessage,
        },
      },
      modelOptions: {updateOn: 'change' as const},
    },
  ];
  form: FormGroup = new UntypedFormGroup({});
  options: FormlyFormOptions = {};
  submit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.form.valid) {
      this.dialogRef.close(this.model.email);
    }
  }
}

@Component({
  imports: [MatIconModule, MatButtonModule, MatDialogModule],
  template: `<h1 mat-dialog-title>Remove Owner</h1>
    <mat-dialog-content>
      Are you sure you want to remove <b>{{ candidate }}</b> as an owner?
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button type="button" [mat-dialog-close]="false"> &#x2717; Cancel</button>
      <button matButton="filled" type="button" [mat-dialog-close]="true">
        <mat-icon class="material-symbols-outlined">delete</mat-icon> Remove</button
      >
    </mat-dialog-actions>`,
})
class RemoveOwnerDialog {
  candidate: string;
  constructor() {
    const data = inject(MAT_DIALOG_DATA);
    this.candidate = data.candidate;
  }
}

@Component({
  selector: 'app-owner-table-form-field',
  imports: [MatTableModule, MatButtonModule, MatIconModule, MatTooltip],
  templateUrl: './owner-table-form-field.component.html',
})
export class OwnerTableFormFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  @Input() showActions: boolean = true;
  @ViewChild('[mat-table]') matTable: MatTable<{email: string; role: string}>;
  me: Signal<string>;
  matData: MatTableDataSource<{email: string; role: 'Owner'}>;

  constructor(
    userService: UserService,
    private readonly matDialog: MatDialog,
    orgService: OrganizationService,
  ) {
    super();
    this.me = computed(() => userService.userSignal()?.email);
    lastValueFrom(orgService.getOrganizations()).then(orgs => SetOrganizations(orgs.organizations));
  }

  ngOnInit(): void {
    this.matData = new MatTableDataSource();
    this.#reload();
  }

  get columns() {
    return this.showActions ? ['email', 'role', 'actions'] : ['email', 'role'];
  }

  isImmortal(row: {email: string; role: 'Owner'}): boolean {
    return !!this.immortalityReason(row);
  }

  immortalityReason(row: {email: string; role: 'Owner'}): string | undefined {
    //@ts-expect-error
    if (this.model[this.key].length == 1) return "Can't remove the last owner.";
    if (row.email == this.me()) return "Can't remove yourself.";
    return undefined;
  }

  async add(): Promise<void> {
    const newOwner: string | undefined = await lastValueFrom(this.matDialog.open(AddOwnerDialog).afterClosed());
    if (!newOwner) return;
    //@ts-expect-error
    if (this.model[this.key].includes(newOwner)) return;
    //@ts-expect-error
    this.model[this.key].push(newOwner);
    this.formControl.markAsDirty();
    this.#reload();
  }

  async remove(row: {email: string; role: 'Owner'}): Promise<void> {
    if (this.isImmortal(row)) return;
    const assent: boolean = await lastValueFrom(
      this.matDialog.open(RemoveOwnerDialog, {data: {candidate: row.email}}).afterClosed(),
    );
    if (!assent) return;
    //@ts-expect-error
    if (this.model[this.key].includes(row.email)) {
      //@ts-expect-error
      this.model[this.key].splice(this.model[this.key].indexOf(row.email), 1);
    }
    this.formControl.markAsDirty();
    this.#reload();
  }

  #reload() {
    //@ts-expect-error
    this.matData.data = this.model[this.key].map(email => ({email, role: 'Owner'}));
    this.matTable?.renderRows();
  }
}
